import type { DeskPackageConfiguration } from '../types/rate-card';
import { clockRange } from './format';

/**
 * How a desk package's rules read. Shared because both apps describe the same
 * package: the admin editing and selling it, the public site advertising it.
 */

/** ISO weekday numbers, Monday = 1. */
export const WEEKDAYS = [
  { value: 1, short: 'Mon' },
  { value: 2, short: 'Tue' },
  { value: 3, short: 'Wed' },
  { value: 4, short: 'Thu' },
  { value: 5, short: 'Fri' },
  { value: 6, short: 'Sat' },
  { value: 7, short: 'Sun' },
] as const;

/** "6 months", "1 day". */
export const packageDurationLabel = (d: DeskPackageConfiguration['duration']) =>
  `${d.count} ${d.unit}${d.count === 1 ? '' : 's'}`;

/** "Mon–Fri", "Every day", "Mon, Wed, Fri". */
export function daysLabel(days: readonly number[]): string {
  const sorted = [...days].sort((a, b) => a - b);
  if (sorted.length === 7) return 'Every day';
  const contiguous = sorted.every((d, i) => i === 0 || d === (sorted[i - 1] as number) + 1);
  const name = (d: number) => WEEKDAYS.find((w) => w.value === d)?.short ?? String(d);
  if (contiguous && sorted.length > 2)
    return `${name(sorted[0] as number)}–${name(sorted[sorted.length - 1] as number)}`;
  return sorted.map(name).join(', ');
}

/** "Mon–Fri · 6:00 PM – 12:00 AM", or the facility's hours when unrestricted. */
export const packageAccessLabel = (access: DeskPackageConfiguration['access']) =>
  access === null
    ? 'Normal opening hours'
    : `${daysLabel(access.daysOfWeek)} · ${clockRange(access.startMinute, access.endMinute)}`;
